/** Cliente de la API. El token vive en sessionStorage: se borra al cerrar la pestaña. */
import type { TipoHallazgo, Superficie } from "../../dominio/src/index.ts";
export interface LicenciaResumen { aplica: boolean; estado: string; nombreEstado: string; dias: number | null; vence: string | null; soloLectura: boolean; plan: string; nombrePlan: string; funciones: string[]; modo?: string }
export interface Usuario { id: string; nombre: string; rol: string; registro: string; clinicaId: string; clinica?: string; permisos: string[]; email?: string; debeCambiar?: boolean; dosPasos?: { activo: boolean; obligatorio: boolean; diasRestantes: number | null }; licencia?: LicenciaResumen; plataforma?: boolean; academico?: boolean }
export interface Sesion { token: string; usuario: Usuario }

export class ErrorApi extends Error {
  estado: number;
  detalles: string[];
  /** el cuerpo completo de la respuesta, por si trae más que el mensaje (por ejemplo, los pacientes parecidos) */
  datos?: Record<string, unknown>;
  constructor(estado: number, mensaje: string, detalles: string[] = [], datos?: Record<string, unknown>) { super(mensaje); this.estado = estado; this.detalles = detalles; this.datos = datos; }
}

const CLAVE = "donna.sesion";
let sesion: Sesion | null = null;
let alExpirar: () => void = () => undefined;

export function leerSesion(): Sesion | null {
  if (sesion) return sesion;
  try { const s = sessionStorage.getItem(CLAVE); sesion = s ? JSON.parse(s) : null; } catch { sesion = null; }
  return sesion;
}
export function guardarSesion(s: Sesion | null) {
  sesion = s;
  try { if (s) sessionStorage.setItem(CLAVE, JSON.stringify(s)); else sessionStorage.removeItem(CLAVE); } catch { /* sin almacenamiento: queda en memoria */ }
}
export function cuandoExpire(fn: () => void) { alExpirar = fn; }
export const puede = (permiso: string) => !!leerSesion()?.usuario.permisos.includes(permiso);
/** ¿El plan de la clínica incluye esta función? (sin planes activos, todo está incluido) */
/** Versión instalada en el computador del consultorio: sin nube de Donna y sin funciones con inteligencia artificial. */
export const modoConsultorio = () => leerSesion()?.usuario.licencia?.modo === "consultorio";
export const incluye = (funcion: string) => { const l = leerSesion()?.usuario.licencia; return !l?.aplica || l.funciones.includes(funcion); };

/** Un laboratorio que trabaja con varias clínicas indica de cuál es la orden que tiene abierta (la bandeja las junta todas). */
let clinicaLab: string | null = null;
export function fijarClinicaLab(id: string | null) { clinicaLab = id; }
const cabeceraClinica = (): Record<string, string> => (clinicaLab ? { "x-clinica": clinicaLab } : {});

export async function api<T = unknown>(metodo: string, ruta: string, cuerpo?: unknown): Promise<T> {
  const s = leerSesion();
  let r: Response;
  try {
    r = await fetch(ruta, {
      method: metodo,
      headers: { ...(s ? { authorization: `Bearer ${s.token}` } : {}), ...cabeceraClinica(), ...(cuerpo !== undefined ? { "content-type": "application/json" } : {}) },
      body: cuerpo !== undefined ? JSON.stringify(cuerpo) : undefined,
    });
  } catch {
    throw new ErrorApi(0, "No hay conexión con el servidor.");
  }
  const texto = await r.text();
  let datos: unknown = null;
  try { datos = texto ? JSON.parse(texto) : null; } catch { /* respuesta no JSON */ }
  if (!r.ok) {
    const e = (datos ?? {}) as { error?: string; detalles?: string[] };
    if (r.status === 401 && s && !ruta.endsWith("/login")) { guardarSesion(null); alExpirar(); }
    throw new ErrorApi(r.status, e.error ?? `Error ${r.status}`, e.detalles ?? [], (datos ?? undefined) as Record<string, unknown> | undefined);
  }
  // avisos del servidor (por ejemplo, se acabaron los dictados con IA del mes)
  const aviso = (datos as { aviso?: unknown } | null)?.aviso;
  if (ruta.startsWith("/api/dictado/") && typeof aviso === "string") window.dispatchEvent(new CustomEvent("donna:aviso", { detail: aviso }));
  return datos as T;
}

/* ---------- tipos que devuelve la API ---------- */
export interface Paciente {
  id: string; hc: number; nombres: string; apellidos: string; doc_tipo: "cedula" | "pasaporte"; doc: string;
  nacimiento: string | null; sexo: "F" | "M" | "O"; telefono: string; email: string; direccion: string; ciudad: string;
  filiacion: Record<string, unknown>; representante: { nombre?: string; parentesco?: string; doc?: string; telefono?: string } | null;
  foto?: string | null;
  /** registro rápido: días que lleva, último día para completar la cédula o el pasaporte y si ya pasó el plazo (solo viene si es registro rápido) */
  ficha_rapida?: { dias: number; limite: string; restan: number; avisar: boolean; vencida: boolean };
  /** paciente de prueba: no cuenta en reportes ni comisiones y no recibe mensajes automáticos */
  es_prueba?: boolean;
  /** agendado solo con nombres y celular; falta completar la ficha y la historia clínica */
  registro_rapido?: boolean;
  /** ficha archivada por estar duplicada: ya no se le agrega nada; lo suyo se ve desde la ficha principal */
  archivada?: { en: string; motivo: string; principal: { id: string; hc: number; nombres: string; apellidos: string } | null };
  /** fichas duplicadas que se archivaron a favor de esta (sus registros siguen consultables) */
  vinculadas?: { id: string; hc: number; nombres: string; apellidos: string; archivado_motivo: string; archivado_en: string; saldo: number; porHacer: number }[];
}
export interface HallazgoApi { id: string; diente: number | null; tipo: TipoHallazgo; estado: "por_realizar" | "realizado"; superficies: Superficie[]; cie10: string | null; nota: string; grado?: number | null }
export interface Indice { cariados: number; perdidos: number; obturados: number; total: number }
export interface Odontograma { hallazgos: HallazgoApi[]; denticion: "temporal" | "mixta" | "permanente"; cpod: Indice; ceod: Indice }
export interface Nota { id: string; tipo: "nota" | "epicrisis"; texto: string; piezas: number[]; firmada_en: string; firmada_por: string; adendas: { id: string; texto: string; autor: string; creado: string }[] }
export interface Historia { anamnesis: Record<string, string>; examen: Record<string, string>; actualizado: string | null }

/** Descarga un binario protegido (p. ej. una foto) y devuelve una URL local para mostrarlo. */
export async function apiBlob(ruta: string): Promise<string> {
  const s = leerSesion();
  const r = await fetch(ruta, { headers: { ...(s ? { authorization: `Bearer ${s.token}` } : {}), ...cabeceraClinica() } });
  if (!r.ok) throw new ErrorApi(r.status, r.status === 404 ? "No se encontró el archivo." : `Error ${r.status}`);
  return URL.createObjectURL(await r.blob());
}

/** Descarga un PDF protegido: devuelve su enlace local (para descargar) y sus bytes (para mostrarlo con el visor propio). */
export async function apiPdf(ruta: string): Promise<{ url: string; bytes: ArrayBuffer }> {
  const s = leerSesion();
  const r = await fetch(ruta, { headers: { ...(s ? { authorization: `Bearer ${s.token}` } : {}), ...cabeceraClinica() } });
  if (!r.ok) throw new ErrorApi(r.status, r.status === 404 ? "No se encontró el archivo." : r.status === 409 ? ((await r.json().catch(() => null))?.error ?? "Este documento ya no está disponible.") : `Error ${r.status}`);
  const blob = await r.blob();
  return { url: URL.createObjectURL(blob), bytes: await blob.arrayBuffer() };
}

export interface Cita {
  id: string; paciente_id: string; profesional_id: string; inicio: string; fin: string; motivo: string; notas: string;
  estado: "agendada" | "confirmada" | "atendida" | "no_asistio" | "cancelada"; confirmada_por: string;
  paciente: string; telefono: string; hc: number; profesional: string; color: string; fecha: string; hora: string; horaFin: string; duracion: number;
  aviso_enviado?: string | null;
  /** material extra anotado en el último control de ortodoncia para esta cita (alerta) */
  alerta_material?: string | null;
  paciente_nuevo?: boolean;
  /** sede de la cita (null en citas sin sede) */
  sede_id: string | null; sede: string | null;
}
/** Resultado de enviar un mensaje ya: por WhatsApp Business (enviado) o con el enlace para enviarlo con un clic. */
export interface EnvioAhora { enviado: boolean; enlace: string | null; canal: "whatsapp" | "email"; destino: string; texto: string; aviso?: string }

/**
 * Envía un mensaje «ahora». La ventana se abre en el mismo clic (si no, el navegador la bloquea) y, si el
 * servidor lo envió por WhatsApp Business, se cierra sola.
 */
export async function enviarMensajeAhora(ruta: string, cuerpo: unknown): Promise<EnvioAhora> {
  const ventana = window.open("", "_blank");
  try {
    const r = await api<EnvioAhora>("POST", ruta, cuerpo);
    if (r.enlace && ventana) ventana.location.href = r.enlace;
    else { ventana?.close(); if (r.enlace) location.href = r.enlace; }
    return r;
  } catch (e) { ventana?.close(); throw e; }
}
export interface Profesional { id: string; nombre: string; rol: string; color: string }
